import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
} from "react";

const NON_DIGITS = /\D/g;

/**
 * Segmented one-time-code field.
 *
 * `value` is always a dense string of digits ("", "12", "123456"): boxes fill left to right
 * and deleting a digit closes the gap, so there is never a half-filled state to reason about.
 *
 *  - typing a digit overwrites the box and moves on; letters are ignored
 *  - paste or browser/phone autofill ("one-time-code") of a full code fills every box
 *  - Backspace / Delete / arrows / Home / End behave like a text field
 *  - `onComplete(code)` fires the moment the last digit lands
 *  - `status` ("idle" | "error" | "success") colours the boxes; bump `shake` to replay the
 *    error shake
 */
const OtpInput = forwardRef(function OtpInput(
  {
    length = 6,
    value,
    onChange,
    onComplete,
    status = "idle",
    disabled = false,
    shake = 0,
    labelledBy,
    describedBy,
  },
  ref
) {
  const inputs = useRef([]);
  const group = useRef(null);

  // The code as of *right now*. A state update only lands on the next render, but focus moves
  // (and fast typing, paste, autofill) happen before that. Everything that decides something
  // synchronously reads this ref, so it never acts on a code that is one keystroke behind.
  // `commit()` moves it ahead immediately; the layout effect re-syncs it after every commit
  // (before the browser can deliver the next event), e.g. when the parent clears the code.
  const valueRef = useRef(value);
  useLayoutEffect(() => {
    valueRef.current = value;
  }, [value]);

  const focusAt = useCallback(
    (index) => {
      const target = inputs.current[Math.max(0, Math.min(length - 1, index))];
      if (target) {
        target.focus();
        target.select();
      }
    },
    [length]
  );

  // Parent can pull focus to the first empty box (or a specific one).
  useImperativeHandle(
    ref,
    () => ({ focus: (index) => focusAt(index ?? valueRef.current.length) }),
    [focusAt]
  );

  // Replay the shake on every new failure (the class is removed again on animationend).
  useEffect(() => {
    const element = group.current;
    if (!shake || !element) return;
    element.classList.remove("is-shaking");
    void element.offsetWidth; // force reflow so the animation restarts
    element.classList.add("is-shaking");
  }, [shake]);

  const commit = (next, focusIndex) => {
    const previous = valueRef.current;
    if (next !== previous) {
      valueRef.current = next; // visible to the focus handler that the line below triggers
      onChange(next);
    }
    focusAt(focusIndex);
    if (next.length === length && next !== previous) onComplete?.(next);
  };

  // Writes `incoming` (1+ digits) starting at box `index`.
  const applyDigits = (index, incoming) => {
    const current = valueRef.current;
    const start = incoming.length >= length ? 0 : Math.min(index, current.length);
    const next =
      incoming.length === 1 && start < current.length
        ? current.slice(0, start) + incoming + current.slice(start + 1)
        : (current.slice(0, start) + incoming).slice(0, length);
    commit(next, Math.min(start + incoming.length, length - 1));
  };

  const removeAt = (index) => {
    const current = valueRef.current;
    return current.slice(0, index) + current.slice(index + 1);
  };

  const handleKeyDown = (index, event) => {
    if (event.metaKey || event.ctrlKey || event.altKey) return; // keep paste / select-all etc.

    if (/^\d$/.test(event.key)) {
      event.preventDefault();
      if (!disabled) applyDigits(index, event.key);
      return;
    }

    const current = valueRef.current;
    switch (event.key) {
      case "Backspace": {
        event.preventDefault();
        if (disabled) return;
        if (index < current.length) {
          commit(removeAt(index), index); // clear this box, stay on it
        } else {
          const last = Math.min(index, current.length);
          if (last > 0) commit(current.slice(0, last - 1), last - 1); // empty box: clear previous
        }
        break;
      }
      case "Delete":
        event.preventDefault();
        if (!disabled && index < current.length) commit(removeAt(index), index);
        break;
      case "ArrowLeft":
        event.preventDefault();
        focusAt(index - 1);
        break;
      case "ArrowRight":
        event.preventDefault();
        focusAt(Math.min(index + 1, current.length));
        break;
      case "Home":
        event.preventDefault();
        focusAt(0);
        break;
      case "End":
        event.preventDefault();
        focusAt(Math.min(current.length, length - 1));
        break;
      default:
        // swallow stray printable characters so a letter never flashes into a box
        if (event.key.length === 1) event.preventDefault();
    }
  };

  // Fallback for input that never produces a usable keydown: mobile keyboards, autofill, IME.
  const handleChange = (index, event) => {
    if (disabled) return;
    const current = valueRef.current;
    const raw = event.target.value;
    const typed = raw.replace(NON_DIGITS, "");

    if (!typed) {
      // box emptied by something other than our keydown (e.g. a mobile Backspace)
      if (raw === "" && index < current.length) commit(removeAt(index), index);
      return;
    }

    let incoming = typed;
    if (current[index] && typed.length === 2) {
      // typed next to an existing digit: keep only the new character
      incoming = typed[0] === current[index] ? typed[1] : typed[0];
    }
    applyDigits(index, incoming);
  };

  const handlePaste = (index, event) => {
    event.preventDefault();
    if (disabled) return;
    const digits = event.clipboardData
      .getData("text")
      .replace(NON_DIGITS, "")
      .slice(0, length);
    if (digits) applyDigits(index, digits);
  };

  const handleFocus = (index, event) => {
    // keep the value dense: never let focus run ahead of the first empty box
    const filled = valueRef.current.length;
    if (index > filled) {
      focusAt(filled);
      return;
    }
    event.target.select();
  };

  const activeIndex = Math.min(value.length, length - 1);

  return (
    <div
      ref={group}
      className="mrc-otp"
      role="group"
      aria-labelledby={labelledBy}
      aria-describedby={describedBy}
      data-status={status}
      onAnimationEnd={(event) => {
        if (event.target === event.currentTarget) {
          event.currentTarget.classList.remove("is-shaking");
        }
      }}
    >
      {Array.from({ length }, (_, index) => {
        const digit = value[index] ?? "";
        return (
          <input
            key={index}
            ref={(element) => {
              inputs.current[index] = element;
            }}
            className={`mrc-otp__digit browser-default${digit ? " is-filled" : ""}`}
            // Roving tab stop: Tab enters the group at the box to fill next and leaves it again.
            // (If every box were tabbable, the focus guard would bounce Tab back: a keyboard trap.)
            tabIndex={index === activeIndex ? 0 : -1}
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            autoComplete="one-time-code"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
            aria-label={`Digit ${index + 1} of ${length}`}
            aria-invalid={status === "error" ? true : undefined}
            readOnly={disabled}
            value={digit}
            onChange={(event) => handleChange(index, event)}
            onKeyDown={(event) => handleKeyDown(index, event)}
            onPaste={(event) => handlePaste(index, event)}
            onFocus={(event) => handleFocus(index, event)}
            onClick={(event) => event.currentTarget.select()}
          />
        );
      })}
    </div>
  );
});

export default OtpInput;
